/**
 * Priority Actions Component
 * Displays 3-card model matching prototype: Urgent Actions, Performance Alerts, Opportunities
 */

import React from 'react';
import {
  AlertTriangle,
  ClipboardList,
  Wallet,
  CheckCircle2,
  TrendingDown,
  BarChart3,
  Zap,
  TrendingUp,
  Receipt,
  Target,
  Lightbulb,
  HelpCircle,
} from 'lucide-react';
import {
  AssetAllocation,
  RiskMetrics,
  StressTests,
  PerformanceMetrics
} from '../../services/api/portfolioApiService';
import { useCanonicalDrift } from '../../hooks/useCanonicalDrift';

interface PriorityActionsProps {
  mode: 'global' | 'client';
  allocation?: AssetAllocation | null;
  risk?: RiskMetrics | null;
  stressTests?: StressTests | null;
  performance?: PerformanceMetrics | null;
  cash?: number;
  portfolioValue?: number;
  /**
   * TASK-723 (review 2026-07-29): required to reach the canonical drift decision. Without it
   * drift is reported as unassessed rather than assumed clean.
   */
  clientId?: string | null;
  onAction: (action: string, data: any) => void;
  loading?: boolean;
}

interface ActionItem {
  icon: React.ReactNode;
  text: string;
  buttonText: string;
  buttonAction: () => void;
  buttonType?: 'primary' | 'danger';
}

const ICON_SIZE = 'w-5 h-5';

const PriorityActions: React.FC<PriorityActionsProps> = ({
  mode: _mode,
  allocation,
  risk,
  stressTests: _stressTests,
  performance,
  cash,
  portfolioValue,
  clientId,
  onAction,
  loading
}) => {
  // TASK-723 (review 2026-07-29): this card read `allocation.rebalance_needed`, retired by
  // the canonical-drift work and now permanently falsy. The drift action stopped appearing
  // AND the empty-list fallback below filled the gap with "All compliance metrics within
  // range" — a removed analysis rendered as a clean bill of health.
  const { needsRebalancing, state: driftState, note: driftNote, breachedSlots } =
    useCanonicalDrift(clientId);

  // Generate urgent actions based on risk and allocation data
  const getUrgentActions = (): ActionItem[] => {
    const actions: ActionItem[] = [];

    if (risk && risk.concentration_risk > 15) {
      actions.push({
        icon: <AlertTriangle className={`${ICON_SIZE} text-amber-500`} />,
        text: `Concentration risk at ${risk.concentration_risk.toFixed(1)}%`,
        buttonText: 'Resolve',
        buttonAction: () => onAction('rebalance', { type: 'concentration' }),
        buttonType: 'danger'
      });
    }

    if (needsRebalancing === true) {
      actions.push({
        icon: <ClipboardList className={`${ICON_SIZE} text-blue-600`} />,
        text:
          breachedSlots.length > 0
            ? `Portfolio rebalancing required — ${breachedSlots.length} allocation ` +
              `${breachedSlots.length === 1 ? 'slot is' : 'slots are'} outside tolerance`
            : 'Portfolio rebalancing required',
        buttonText: 'Create Task',
        buttonAction: () => onAction('rebalance', { allocation })
      });
    }

    // Drift that could not be assessed is stated, not omitted. Omitting it is what let the
    // fallback below report an absent analysis as compliance.
    if (driftState !== 'loading' && needsRebalancing === null) {
      actions.push({
        icon: <HelpCircle className={`${ICON_SIZE} text-gray-400`} />,
        text:
          driftState === 'failed'
            ? `Allocation drift could not be assessed${driftNote ? ` — ${driftNote}` : ''}`
            : `Allocation drift cannot be assessed${driftNote ? ` — ${driftNote}` : ''}`,
        buttonText: 'Review Profile',
        buttonAction: () => onAction('review-drift', { state: driftState, note: driftNote })
      });
    }

    if (cash && portfolioValue) {
      const cashPercentage = (cash / portfolioValue) * 100;
      if (cashPercentage < 2) {
        actions.push({
          icon: <Wallet className={`${ICON_SIZE} text-yellow-600`} />,
          text: `Low liquidity: ${cashPercentage.toFixed(1)}%`,
          buttonText: 'Increase Cash',
          buttonAction: () => onAction('increase-cash', { current: cashPercentage })
        });
      }
    }

    if (actions.length === 0) {
      // Never "All compliance metrics within range": this card checks concentration, drift
      // and liquidity, and it has no view of the compliance engine at all. It now names the
      // checks it actually ran — and it cannot reach this branch while drift is unknown,
      // because the unassessed row above is already in the list.
      actions.push({
        icon: <CheckCircle2 className={`${ICON_SIZE} text-green-600`} />,
        text:
          driftState === 'loading'
            ? 'Checking allocation drift…'
            : 'No urgent allocation, concentration or liquidity issues',
        buttonText: 'View Details',
        buttonAction: () => onAction('view-compliance', {})
      });
    }

    return actions.slice(0, 3);
  };

  // Generate performance alerts
  const getPerformanceAlerts = (): ActionItem[] => {
    const alerts: ActionItem[] = [];

    if (performance) {
      if (performance.total_return != null && performance.total_return < 0) {
        alerts.push({
          icon: <TrendingDown className={`${ICON_SIZE} text-red-500`} />,
          text: `Portfolio down ${Math.abs(performance.total_return).toFixed(1)}%`,
          buttonText: 'Run Analysis',
          buttonAction: () => onAction('analyze', { type: 'performance' })
        });
      }

      if (performance.sharpe_ratio != null && performance.sharpe_ratio < 0.5) {
        alerts.push({
          icon: <BarChart3 className={`${ICON_SIZE} text-blue-600`} />,
          text: `Low Sharpe ratio: ${performance.sharpe_ratio.toFixed(2)}`,
          buttonText: 'Analyze',
          buttonAction: () => onAction('review', { metric: 'sharpe' })
        });
      }

      if (performance.volatility != null && performance.volatility > 20) {
        alerts.push({
          icon: <Zap className={`${ICON_SIZE} text-amber-500`} />,
          text: `High volatility: ${performance.volatility.toFixed(1)}%`,
          buttonText: 'Stabilize',
          buttonAction: () => onAction('stabilize', { volatility: performance.volatility })
        });
      }
    }

    if (performance && performance.total_return != null && performance.total_return > 5) {
      alerts.push({
        icon: <TrendingUp className={`${ICON_SIZE} text-green-600`} />,
        text: `Outperforming +${performance.total_return.toFixed(1)}%`,
        buttonText: 'Rebalance',
        buttonAction: () => onAction('rebalance', { type: 'profit-taking' })
      });
    }

    if (alerts.length === 0) {
      alerts.push({
        icon: <BarChart3 className={`${ICON_SIZE} text-blue-600`} />,
        text: 'Performance metrics on track',
        buttonText: 'View Report',
        buttonAction: () => onAction('view-performance', {})
      });
    }

    return alerts.slice(0, 3);
  };

  // Generate opportunities
  const getOpportunities = (): ActionItem[] => {
    const opportunities: ActionItem[] = [];

    if (performance && performance.total_return != null && performance.total_return < -5) {
      opportunities.push({
        icon: <Receipt className={`${ICON_SIZE} text-purple-600`} />,
        text: 'Tax loss harvesting available',
        buttonText: 'Run Scenario',
        buttonAction: () => onAction('tax-loss-harvesting', {})
      });
    }

    if (needsRebalancing === true) {
      opportunities.push({
        icon: <Target className={`${ICON_SIZE} text-blue-600`} />,
        text: 'Rebalancing opportunity detected',
        buttonText: 'Run Scenario',
        buttonAction: () => onAction('strategic-rebalance', { allocation })
      });
    }

    if (opportunities.length === 0) {
      // "None detected" is only true of the checks that ran. With drift unassessed the
      // rebalancing opportunity above could not be evaluated at all, and saying nothing about
      // that turns a gap into an answer.
      opportunities.push({
        icon: <Lightbulb className={`${ICON_SIZE} text-gray-400`} />,
        text:
          needsRebalancing === null && driftState !== 'loading'
            ? 'No opportunities from the checks that completed — allocation drift was not assessed'
            : 'No actionable opportunities detected',
        buttonText: 'View Portfolio',
        buttonAction: () => onAction('view-portfolio', {})
      });
    }

    return opportunities.slice(0, 3);
  };

  if (loading) {
    return (
      <section className="mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-lg p-6">
              <div className="animate-pulse">
                <div className="h-10 bg-gray-200 rounded mb-4"></div>
                <div className="space-y-3">
                  <div className="h-12 bg-gray-100 rounded"></div>
                  <div className="h-12 bg-gray-100 rounded"></div>
                  <div className="h-12 bg-gray-100 rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  const urgentActions = getUrgentActions();
  const performanceAlerts = getPerformanceAlerts();
  const opportunities = getOpportunities();

  const renderActionList = (items: ActionItem[], defaultButtonType: 'primary' | 'danger' = 'primary') => (
    items.map((item, index) => (
      <div
        key={index}
        className={`flex items-center gap-3 py-3 ${
          index < items.length - 1 ? 'border-b border-gray-200' : ''
        }`}
      >
        <span className="flex-shrink-0">{item.icon}</span>
        <span className="flex-1 text-sm text-gray-700">{item.text}</span>
        <button
          className={`px-3 py-1.5 rounded text-xs font-semibold transition-opacity hover:opacity-90 ${
            (item.buttonType || defaultButtonType) === 'danger'
              ? 'bg-red-600 text-white'
              : 'bg-[#F7B500] text-[#002B45]'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            item.buttonAction();
          }}
        >
          {item.buttonText}
        </button>
      </div>
    ))
  );

  return (
    <section className="mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Urgent Actions Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow">
          <div className="p-4 font-semibold bg-red-600 text-white flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Urgent Actions
          </div>
          <div className="p-6">
            {renderActionList(urgentActions, 'danger')}
          </div>
        </div>

        {/* Performance Alerts Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow">
          <div className="p-4 font-semibold bg-amber-400 text-[#002B45] flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Performance Alerts
          </div>
          <div className="p-6">
            {renderActionList(performanceAlerts)}
          </div>
        </div>

        {/* Opportunities Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow">
          <div className="p-4 font-semibold bg-green-600 text-white flex items-center gap-2">
            <Lightbulb className="w-4 h-4" />
            Opportunities
          </div>
          <div className="p-6">
            {renderActionList(opportunities)}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PriorityActions;
